import { ApiBase } from "@/api/ApiBase";
import type { TipoDesconto } from "@/api/facturacao/DocumentosApi";
import type {
  ContaBancariaResumo,
  PaisResumo,
} from "@/api/facturacao/AuxiliarApi";
import type { MesaResumo } from "@/api/config/MesasApi";

export interface ArtigoParaVendaPOS {
  id: number;
  tipo: "P" | "S";
  nome: string;
  nomeCategoria: string;
  codigo: string;
  preco: number;
  retencao: "Sujeito" | "NA";
  taxa: number;
  siglaTaxa: string;
  foto: string | null;
  qtdStock: number;
  codigobarras: string | null;
}

export interface LinhaCarrinhoPOS {
  id: number;
  tipo: "P" | "S";
  reserva_id: number | null;
  nome: string;
  nomeCategoria: string;
  codigo: string;
  qtd: number;
  preco: number;
  retencao: "Sujeito" | "NA";
  desconto: number;
  tipoDesconto: TipoDesconto;
  taxa: number;
  siglaTaxa: string;
  codigoImposto: string;
  foto: string | null;
  codigobarras: string | null;
}

export type AccaoCarrinho =
  | "mais"
  | "menos"
  | "alterar"
  | "alterar-desconto"
  | "apagar"
  | "abandonar";

export interface RespostaCarrinhoPOS {
  msg: string;
  tipo: "sucesso" | "erro";
  artigos: LinhaCarrinhoPOS[];
}

export interface ReservaPOS {
  id: number;
  codigo: string;
  referencia: string | null;
  lugaresOcupados: number;
  estado: "Aberto" | "Fechado";
  motivoFecho: string;
  idMesa: number;
  nomeMesa: string;
  idCliente: number;
  nomeCliente: string;
  dataCriacao: string;
}

export interface RespostaReservaPOS {
  msg: string;
  tipo: "sucesso" | "erro";
  reservas?: ReservaPOS[];
  mesas?: MesaResumo[];
}

export interface EssencialTerminaisPos {
  contasBancarias: ContaBancariaResumo[];
  paises: PaisResumo[];
  reservas?: ReservaPOS[];
}

export interface NovoClientePOS {
  nome: string;
  nif: string;
  tipo: "Particular" | "Empresa";
  estadoCivil?: string;
  genero?: string;
  paisId: number;
  telefone: string;
  email: string;
  provincia: string;
  endereco: string;
}

export interface RespostaClientePOS {
  msg: string;
  tipo: "sucesso" | "erro";
}

export class TerminalPosApi extends ApiBase {
  essencialToTerminaisPos(): Promise<EssencialTerminaisPos> {
    return this.http.put<EssencialTerminaisPos>(
      "/api/facturacao/info-auxiliar/essencial-to-terminais-pos",
    );
  }

  listarArtigosPOS(): Promise<ArtigoParaVendaPOS[]> {
    return this.http.put<ArtigoParaVendaPOS[]>(
      "/api/facturacao/artigo/lista-by-comerciante-to-doc/pos/0/0",
    );
  }

  crudCarrinhoPadrao(
    accao: AccaoCarrinho,
    dados: {
      artigoId: number;
      qtd?: number;
      desconto?: number;
      tipoDesconto?: TipoDesconto;
    },
  ): Promise<RespostaCarrinhoPOS> {
    // O backend usa qtd directamente ao inserir a 1ª linha (accão "mais"
    // sobre um artigo ainda não no carrinho), por isso tem de vir sempre
    // preenchido — nunca omitir este campo.
    const params: Record<string, string> = {
      artigoId: String(dados.artigoId),
      qtd: String(dados.qtd ?? 1),
    };
    if (dados.desconto !== undefined) params.desconto = String(dados.desconto);
    if (dados.tipoDesconto) params.tipoDesconto = dados.tipoDesconto;
    return this.http.post<RespostaCarrinhoPOS>(
      `/api-pos/artigo-sessao/crud-artigo-temporario/${accao}`,
      params,
    );
  }

  crudCarrinhoRestaurante(
    accao: AccaoCarrinho,
    reservaCode: string,
    dados: {
      artigoId: number;
      qtd?: number;
      desconto?: number;
      tipoDesconto?: TipoDesconto;
    },
  ): Promise<RespostaCarrinhoPOS> {
    const params: Record<string, string> = {
      artigoId: String(dados.artigoId),
      reservaCode,
      qtd: String(dados.qtd ?? 1),
    };
    if (dados.desconto !== undefined) params.desconto = String(dados.desconto);
    if (dados.tipoDesconto) params.tipoDesconto = dados.tipoDesconto;
    return this.http.post<RespostaCarrinhoPOS>(
      `/api-pos/artigo-sessao/crud-artigo-temporario-restaurante/${accao}`,
      params,
    );
  }

  listaCarrinhoPadrao(): Promise<LinhaCarrinhoPOS[]> {
    return this.http.put<LinhaCarrinhoPOS[]>(
      "/api-pos/artigo-sessao/lista-artigos-processo-venda-pos",
    );
  }

  listaCarrinhoRestaurante(reservaCode: string): Promise<LinhaCarrinhoPOS[]> {
    return this.http.post<LinhaCarrinhoPOS[]>(
      "/api-pos/artigo-sessao/lista-artigos-processo-venda-pos-restaurante",
      { reservaCode },
    );
  }

  abrirReserva(dados: {
    mesaCodigo: string;
    clienteId: number;
    referencia?: string | null;
    lugaresOcupados?: number;
  }): Promise<RespostaReservaPOS> {
    const params: Record<string, string> = {
      "mesa.codigo": dados.mesaCodigo,
      "cliente.id": String(dados.clienteId),
      lugaresOcupados: String(dados.lugaresOcupados ?? 1),
    };
    if (dados.referencia) params.referencia = dados.referencia;
    return this.http.post<RespostaReservaPOS>(
      "/api-pos/reserva/abrir",
      params,
    );
  }

  fecharReserva(dados: {
    codigo: string;
    motivoFecho: "Pago" | "Anulado";
  }): Promise<RespostaReservaPOS> {
    return this.http.post<RespostaReservaPOS>("/api-pos/reserva/fechar", {
      codigo: dados.codigo,
      motivoFecho: dados.motivoFecho,
    });
  }

  listaReservasAbertas(): Promise<ReservaPOS[]> {
    return this.http.put<ReservaPOS[]>(
      "/api-pos/reserva/lista-abertas-by-comerciante",
    );
  }

  adicionarClientePOS(dados: NovoClientePOS): Promise<RespostaClientePOS> {
    const params: Record<string, string> = {
      nome: dados.nome,
      nif: dados.nif,
      tipo: dados.tipo,
      "pais.id": String(dados.paisId),
      telefone: dados.telefone,
      email: dados.email,
      provincia: dados.provincia,
      endereco: dados.endereco,
    };
    if (dados.estadoCivil) params.estadoCivil = dados.estadoCivil;
    if (dados.genero) params.genero = dados.genero;
    return this.http.post<RespostaClientePOS>(
      "/api/facturacao/parceiro/add-cliente-pos",
      params,
    );
  }
}
